import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FileText, X, Loader2, Eye, RotateCcw, ChevronRight, Code, Save } from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../context/ToastContext';

interface Template {
  type: string;
  label: string;
  has_custom: boolean;
  is_enabled: boolean;
  subject: string | null;
  updated_at: string | null;
}

interface TemplateDetail {
  type: string;
  custom: {
    id: number;
    subject: string;
    html_content: string;
    is_enabled: boolean;
    updated_at: string;
  } | null;
  default: {
    subject: string;
    html: string;
  };
  variables: Record<string, string>;
}

interface Props {
  agencyId: string;
  agencyName: string;
}

const AgencyEmailTemplatesTab: React.FC<Props> = ({ agencyId, agencyName }) => {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [templateDetail, setTemplateDetail] = useState<TemplateDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editHtml, setEditHtml] = useState('');
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [hoveredTemplate, setHoveredTemplate] = useState<string | null>(null);
const [focusedInput, setFocusedInput] = useState<string | null>(null);
  const [flashedChip, setFlashedChip] = useState<string | null>(null);
  const [hoveredChip, setHoveredChip] = useState<string | null>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const htmlRef = useRef<HTMLTextAreaElement>(null);
  const lastFocusedField = useRef<'subject' | 'html'>('html');

  const insertVariable = useCallback((key: string) => {
    const token = `{{${key}}}`;
    const isSubject = lastFocusedField.current === 'subject';
    const el = isSubject ? subjectRef.current : htmlRef.current;
    const setValue = isSubject ? setEditSubject : setEditHtml;
    const currentValue = isSubject ? editSubject : editHtml;

    if (el) {
      const start = el.selectionStart ?? currentValue.length;
      const end = el.selectionEnd ?? currentValue.length;
      const newValue = currentValue.slice(0, start) + token + currentValue.slice(end);
      setValue(newValue);
      // Restore cursor after React re-render
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + token.length, start + token.length);
      });
    } else {
      setValue(currentValue + token);
    }

    // Flash the chip
    setFlashedChip(key);
    setTimeout(() => setFlashedChip(null), 400);
  }, [editSubject, editHtml]);

  useEffect(() => {
    loadTemplates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyId]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/agencies/${agencyId}/email-templates`);
      setTemplates(response.data.templates);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateDetail = async (type: string) => {
    try {
      setLoadingDetail(true);
      setSelectedTemplate(type);
      const response = await apiService.get(`/agencies/${agencyId}/email-templates/${type}`);
      setTemplateDetail(response.data);
      
      // Set edit values
      if (response.data.custom) {
        setEditSubject(response.data.custom.subject);
        setEditHtml(response.data.custom.html_content);
      } else {
        setEditSubject(response.data.default.subject);
        setEditHtml(response.data.default.html);
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to load template');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleToggle = async (type: string, currentEnabled: boolean) => {
    try {
      await apiService.post(`/agencies/${agencyId}/email-templates/${type}/toggle`, {
        is_enabled: !currentEnabled
      });
      showSuccess(`Template ${!currentEnabled ? 'enabled' : 'disabled'}`);
      loadTemplates();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to toggle template');
    }
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;
    
    try {
      setSaving(true);
      await apiService.put(`/agencies/${agencyId}/email-templates/${selectedTemplate}`, {
        subject: editSubject,
        html_content: editHtml,
        is_enabled: true
      });
      showSuccess('Template saved successfully');
      loadTemplates();
      loadTemplateDetail(selectedTemplate);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedTemplate || !templateDetail?.custom) return;
    
    if (!window.confirm('Reset this template to the default? Your customizations will be lost.')) {
      return;
    }

    try {
      await apiService.delete(`/agencies/${agencyId}/email-templates/${selectedTemplate}`);
      showSuccess('Template reset to default');
      loadTemplates();
      loadTemplateDetail(selectedTemplate);
    } catch (err: any) {
      if (err.response?.status === 404) {
        showError('No custom template to reset');
      } else {
        showError(err.response?.data?.error || 'Failed to reset template');
      }
    }
  };

  const handlePreview = async () => {
    if (!selectedTemplate) return;

    try {
      const response = await apiService.post(`/agencies/${agencyId}/email-templates/${selectedTemplate}/preview`, {
        subject: editSubject,
        html_content: editHtml
      });
      setPreviewHtml(response.data.html);
      setShowPreview(true);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to generate preview');
    }
  };

  const inputStyle = (focused: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'var(--v2-surface)',
    border: `1px solid ${focused ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.875rem',
    color: 'var(--v2-text)',
    outline: 'none',
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    boxShadow: focused ? '0 0 0 3px var(--v2-primary-muted)' : 'none'
  });

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--v2-text)',
    marginBottom: '6px'
  };

  const buttonStyle = (hovered: boolean, variant: 'primary' | 'secondary' = 'primary'): React.CSSProperties => ({
    padding: '10px 20px',
    backgroundColor: variant === 'primary'
      ? hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)'
      : hovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
    color: variant === 'primary' ? '#ffffff' : 'var(--v2-text)',
    border: variant === 'primary' ? 'none' : '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  });

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Loader2 size={24} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} color="var(--v2-primary)" />
        <p style={{ color: 'var(--v2-text-tertiary)', marginTop: '12px' }}>Loading templates...</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '20px' }}>
      {/* Template List */}
      <div style={{
        backgroundColor: 'var(--v2-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--v2-border)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--v2-border)',
          backgroundColor: 'var(--v2-surface-alt)'
        }}>
          <h3 style={{ fontSize: '0.9375rem', fontWeight: 500, color: 'var(--v2-text)', margin: 0 }}>
            Email Templates
          </h3>
          <p style={{ fontSize: '0.75rem', color: 'var(--v2-text)', margin: '4px 0 0 0' }}>
            {templates.filter(t => t.has_custom).length} customized
          </p>
        </div>
        
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {templates.map((template) => (
            <div
              key={template.type}
              onClick={() => loadTemplateDetail(template.type)}
              onMouseEnter={() => setHoveredTemplate(template.type)}
              onMouseLeave={() => setHoveredTemplate(null)}
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid var(--v2-border)',
                cursor: 'pointer',
                backgroundColor: selectedTemplate === template.type 
                  ? 'var(--v2-primary-light)' 
                  : hoveredTemplate === template.type 
                    ? 'var(--v2-surface-hover)' 
                    : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'background-color 0.1s'
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: selectedTemplate === template.type ? 'var(--v2-primary)' : 'var(--v2-text)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {template.label}
                  </span>
                  {template.has_custom && (
                    <span style={{
                      fontSize: '0.625rem',
                      padding: '2px 6px',
                      backgroundColor: 'var(--v2-primary-muted)',
                      color: 'var(--v2-primary)',
                      borderRadius: 'var(--radius-full)',
                      fontWeight: 600
                    }}>
                      Custom
                    </span>
                  )}
                </div>
                {!template.is_enabled && (
                  <span style={{
                    fontSize: '0.875rem',
                    color: 'var(--v2-text-muted)'
                  }}>
                    Disabled
                  </span>
                )}
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle(template.type, template.is_enabled);
                  }}
                  style={{
                    width: '36px',
                    height: '20px',
                    borderRadius: '10px',
                    border: 'none',
                    backgroundColor: template.is_enabled ? 'var(--v2-success)' : 'var(--v2-border)',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'background-color 0.2s'
                  }}
                >
                  <span style={{
                    position: 'absolute',
                    top: '2px',
                    left: template.is_enabled ? '18px' : '2px',
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    backgroundColor: '#fff',
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                  }} />
                </button>
                <ChevronRight size={16} color="var(--v2-text-muted)" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Template Editor */}
      <div style={{
        backgroundColor: 'var(--v2-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--v2-border)',
        overflow: 'hidden'
      }}>
        {!selectedTemplate ? (
          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
            <FileText size={48} strokeWidth={1} color="var(--v2-text-muted)" />
            <p style={{ color: 'var(--v2-text-tertiary)', marginTop: '16px' }}>
              Select a template to edit
            </p>
          </div>
        ) : loadingDetail ? (
          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
            <Loader2 size={24} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} color="var(--v2-primary)" />
            <p style={{ color: 'var(--v2-text-tertiary)', marginTop: '12px' }}>Loading template...</p>
          </div>
        ) : templateDetail && (
          <>
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--v2-border)',
              backgroundColor: 'var(--v2-surface-alt)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ fontSize: '0.9375rem', fontWeight: 500, color: 'var(--v2-text)', margin: 0 }}>
                  {templates.find(t => t.type === selectedTemplate)?.label}
                </h3>
                {templateDetail.custom && (
                  <p style={{ fontSize: '0.75rem', color: 'var(--v2-text)', margin: '4px 0 0 0' }}>
                    Last updated: {new Date(templateDetail.custom.updated_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handlePreview}
                  onMouseEnter={() => setHoveredButton('preview')}
                  onMouseLeave={() => setHoveredButton(null)}
                  style={buttonStyle(hoveredButton === 'preview', 'secondary')}
                >
                  <Eye size={16} strokeWidth={1.5} />
                  Preview
                </button>
                <button
                  onClick={handleReset}
                  disabled={!templateDetail?.custom}
                  onMouseEnter={() => setHoveredButton('reset')}
                  onMouseLeave={() => setHoveredButton(null)}
                  style={{
                    ...buttonStyle(hoveredButton === 'reset', 'secondary'),
                    opacity: templateDetail?.custom ? 1 : 0.4,
                    cursor: templateDetail?.custom ? 'pointer' : 'not-allowed'
                  }}
                >
                  <RotateCcw size={16} strokeWidth={1.5} />
                  Reset
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  onMouseEnter={() => setHoveredButton('save')}
                  onMouseLeave={() => setHoveredButton(null)}
                  style={{
                    ...buttonStyle(hoveredButton === 'save', 'primary'),
                    opacity: saving ? 0.6 : 1
                  }}
                >
                  {saving ? (
                    <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <Save size={16} strokeWidth={1.5} />
                  )}
                  Save
                </button>
              </div>
            </div>

            <div style={{ padding: '20px' }}>
              {/* Subject */}
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Subject Line</label>
<input
                  ref={subjectRef}
                  type="text"
                  value={editSubject}
                  onChange={(e) => setEditSubject(e.target.value)}
                  onFocus={() => { setFocusedInput('subject'); lastFocusedField.current = 'subject'; }}
                  onBlur={() => setFocusedInput(null)}
                  style={inputStyle(focusedInput === 'subject')}
                />
              </div>

              {/* HTML Editor */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>HTML Content</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Code size={12} color="var(--v2-text-muted)" />
                    <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-muted)' }}>HTML</span>
                  </div>
                </div>
<textarea
                  ref={htmlRef}
                  value={editHtml}
                  onChange={(e) => setEditHtml(e.target.value)}
                  onFocus={() => { setFocusedInput('html'); lastFocusedField.current = 'html'; }}
                  onBlur={() => setFocusedInput(null)}
                  style={{
                    ...inputStyle(focusedInput === 'html'),
                    minHeight: '300px',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: '0.8125rem',
                    lineHeight: 1.5,
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Variables Reference */}
<div style={{
                padding: '16px',
                backgroundColor: 'var(--v2-surface-alt)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--v2-border)'
              }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--v2-text)', margin: '0 0 4px 0' }}>
                  Click to insert
                </h4>
                <p style={{ fontSize: '0.75rem', color: 'var(--v2-text-muted)', margin: '0 0 12px 0' }}>
                  Place your cursor in the subject or HTML editor, then click a variable.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {templateDetail.variables && Object.entries(templateDetail.variables).map(([key, desc]) => {
                    const isHovered = hoveredChip === key;
                    const isFlashed = flashedChip === key;
                    return (
                      <span
                        key={key}
                        title={desc}
                        onClick={() => insertVariable(key)}
                        onMouseEnter={() => setHoveredChip(key)}
                        onMouseLeave={() => setHoveredChip(null)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: isFlashed
                            ? 'var(--v2-primary-muted)'
                            : isHovered
                              ? 'var(--v2-primary-light)'
                              : 'var(--v2-surface)',
                          border: `1px solid ${isHovered || isFlashed ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
                          borderRadius: 'var(--radius-sm)',
                          fontSize: '0.75rem',
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                          color: 'var(--v2-primary)',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s, border-color 0.15s',
                          userSelect: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        {isHovered && !isFlashed && (
                          <span style={{ fontSize: '0.7rem', fontFamily: 'sans-serif', opacity: 0.7 }}>+</span>
                        )}
                        {key}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <>
          <div
            onClick={() => setShowPreview(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999
            }}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '80vh',
            backgroundColor: 'var(--v2-surface)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-xl)',
            zIndex: 1000,
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--v2-border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Email Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={20} color="var(--v2-text-muted)" />
              </button>
            </div>
            <div style={{ padding: '20px', maxHeight: 'calc(80vh - 60px)', overflowY: 'auto' }}>
              <iframe
                srcDoc={previewHtml}
                style={{
                  width: '100%',
                  height: '500px',
                  border: '1px solid var(--v2-border)',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: '#fff'
                }}
                title="Email Preview"
              />
            </div>
          </div>
        </>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AgencyEmailTemplatesTab;
